import { Component, OnDestroy, OnInit } from '@angular/core';
import { MaintenanceFilter } from '@core/interfaces';
import { SidenavService } from '@core/services/sidenav.service';
import { fuseAnimations } from '@fuse/animations';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import moment from 'moment';
import { merge, Observable, Subject } from 'rxjs';
import { concatMap, debounceTime, tap } from 'rxjs/operators';
import { ReportFilterState } from '../components/filter/services/report-filter-state.service';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';
import { DashboardEquipmentsData, QueryDashboardEquipmentsDataGQL } from './services/api/queries.service';

export interface DashboardEquipmentsDataMapped {
  mtbf: number;
  mttr: number;
  availability: number;
}

@Component({
  templateUrl: './dashboard-equipments.component.html',
  styleUrls: ['./dashboard-equipments.component.scss'],
  animations: [fuseAnimations],
})
export class DashboardEquipmentsComponent implements OnInit, OnDestroy {

  isLoading = true;
  equipmentsIndicatorData?: DashboardEquipmentsDataMapped = null;
  isFilterOn: Subject<boolean>;

  private subscriptions = new Subject<void>();
  private reloadEvent = new Subject<void>();

  constructor(
    public sidenavService: SidenavService,
    private fuseTranslationLoaderService: FuseTranslationLoaderService,
    public reportFilterState: ReportFilterState,
    private fuseProgressBarService: FuseProgressBarService,
    private queryDashboardEquipmentsDataGQL: QueryDashboardEquipmentsDataGQL
  ) { }

  ngOnInit(): void {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });

    this.isFilterOn = this.reportFilterState.isFilterOn;

    merge(this.reportFilterState.onChangeFilter, this.reloadEvent)
      .pipe(debounceTime(200))
      .pipe(tap(() => this.showLoading()))
      .pipe(concatMap(() => this.updateData()))
      .pipe(tap(() => this.hideLoading()))
      .subscribe();

    this.reload();

  }

  ngOnDestroy(): void {
    this.subscriptions.next();
    this.subscriptions.complete();
  }

  reload(): void {
    this.reloadEvent.next();
  }

  clearFilters(): void {
    this.reportFilterState.onClearFilter.next();
  }

  private async updateData(): Promise<void> {
    const { data } = await this.queryDashboardEquipmentsDataGQL
      .fetch({ where: this.getWhere(this.reportFilterState.query) })
      .toPromise();

    this.equipmentsIndicatorData = this.mapMaintenanceStaticsData(data);
  }

  private mapMaintenanceStaticsData(data: DashboardEquipmentsData): DashboardEquipmentsDataMapped {
    const { items } = data.accountMaintenancesStatistics;
    return {
      mtbf: this.findItemValue(items, 'mtbf'),
      mttr: this.findItemValue(items, 'mttr'),
      availability: this.findItemValue(items, 'availability')
    };
  }

  private findItemValue(items: any[], key: string): number {
    const foundItem = items.find((item) => item.key === key);
    return foundItem ? foundItem.value : 0;
  }

  private showLoading(): void {
    this.isLoading = true;
    this.fuseProgressBarService.show();
  }

  private hideLoading(): void {
    this.isLoading = false;
    this.fuseProgressBarService.hide();
  }

  getWhere(query: MaintenanceFilter): Observable<Object> {
    if (!query.openedAt_gte) {
      query.openedAt_gte = moment().subtract(1, 'month').startOf('days').toDate();
    }
    if (!query.openedAt_lte) {
      query.openedAt_lte = moment().startOf('days').toDate();
    }

    const where: any = {
      from: moment(query.openedAt_gte).startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
      to: moment(query.openedAt_lte).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
    };

    if (query.locationId_in && query.locationId_in.length) {
      where.locationId_in = query.locationId_in;
    }

    if (query.locationGroupId_in && query.locationGroupId_in.length) {
      where.locationGroupId_in = query.locationGroupId_in;
    }

    if (query.companyId_in && query.companyId_in.length) {
      where.companyId_in = query.companyId_in;
    }

    if (query.segmentId_in && query.segmentId_in.length) {
      where.segmentId_in = query.segmentId_in;
    }

    if (query.typeId_in && query.typeId_in.length) {
      where.typeId_in = query.typeId_in;
    }

    if (query.scheduledAt_gte) {
      where.scheduledAt_gte = query.scheduledAt_gte;
    }

    if (query.scheduledAt_lte) {
      where.scheduledAt_lte = query.scheduledAt_lte;
    }

    if (query.closedAt_gte) {
      where.closedAt_gte = query.closedAt_gte;
    }

    if (query.closedAt_lte) {
      where.closedAt_lte = query.closedAt_lte;
    }

    return where;
  }
}
